import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { getApiBaseUrl } from '../apiBase';
import OwnerPageLayout from '../components/layout/OwnerPageLayout';
import { useTranslation } from 'react-i18next';


const fmt = (n) => (n == null ? '—' : new Intl.NumberFormat('ru-RU').format(n));

function adminApi() {
    const token = localStorage.getItem('token');
    return axios.create({
        baseURL: getApiBaseUrl(),
        headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
}

const card = {
    background: '#fff',
    borderRadius: '14px',
    padding: '20px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
    border: '1px solid var(--sp-gray-100)'
};

export default function SuperAdminSchoolBillingPage() {
    const { t } = useTranslation(['platformBilling', 'common', 'nav', 'messages', 'errors']);
    const role = localStorage.getItem('role') || '';
    const [schools, setSchools] = useState([]);
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('');
    const [editSchoolId, setEditSchoolId] = useState(null);
    const [detail, setDetail] = useState(null);
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState('');

    useEffect(() => {
        if (role !== 'Admin') return;
        loadOverview();
    }, [role, t]);

    async function loadOverview() {
        setLoading(true);
        try {
            const api = adminApi();
            const [overview, catalog] = await Promise.all([
                api.get('/admin/schools/billing-overview'),
                api.get('/admin/platform/plans')
            ]);
            setSchools(overview.data || []);
            setPlans(catalog.data || []);
        } catch {
            setSchools([]);
            setMsg(t('platformBilling:superAdminLoadError'));
        }
        setLoading(false);
    }

    async function openEdit(schoolId) {
        setEditSchoolId(schoolId);
        setMsg('');
        try {
            const res = await adminApi().get(`/admin/schools/${schoolId}/platform-billing`);
            setDetail(res.data);
            const sub = res.data.subscription;
            const pr = sub.pricing || {};
            setForm({
                planId: sub.planId,
                billingPeriod: sub.billingPeriod || 'monthly',
                baseStudentLimit: sub.baseStudentLimit,
                extraStudentPricePerMonth: sub.extraStudentPricePerMonth,
                hasCustomPricing: pr.hasCustomPricing || false,
                customMonthlyPrice: pr.customMonthlyPrice ?? '',
                customAnnualPrice: pr.customAnnualPrice ?? '',
                adminPricingNote: pr.adminPricingNote || '',
                extendAccessMonths: ''
            });
        } catch {
            setMsg(t('platformBilling:superAdminSchoolLoadError'));
            setEditSchoolId(null);
        }
    }

    function closeEdit() {
        setEditSchoolId(null);
        setDetail(null);
        setForm(null);
    }

    function onPlanChange(planId) {
        const p = (detail?.catalogPlans || plans).find((x) => x.id === Number(planId));
        if (!p || !form) return;
        setForm({
            ...form,
            planId: p.id,
            baseStudentLimit: p.baseStudentLimit,
            extraStudentPricePerMonth: p.extraStudentPricePerMonth,
            customMonthlyPrice: form.hasCustomPricing ? form.customMonthlyPrice : '',
            customAnnualPrice: form.hasCustomPricing ? form.customAnnualPrice : ''
        });
    }

    async function save() {
        if (!editSchoolId || !form) return;
        setSaving(true);
        setMsg('');
        try {
            const body = {
                planId: Number(form.planId),
                billingPeriod: form.billingPeriod,
                baseStudentLimit: form.baseStudentLimit === '' ? null : Number(form.baseStudentLimit),
                extraStudentPricePerMonth: form.extraStudentPricePerMonth === ''
                    ? null
                    : Number(form.extraStudentPricePerMonth),
                hasCustomPricing: form.hasCustomPricing,
                customMonthlyPrice: form.hasCustomPricing && form.customMonthlyPrice !== ''
                    ? Number(form.customMonthlyPrice)
                    : null,
                customAnnualPrice: form.hasCustomPricing && form.customAnnualPrice !== ''
                    ? Number(form.customAnnualPrice)
                    : null,
                adminPricingNote: form.adminPricingNote || null,
                clearCustomPricing: !form.hasCustomPricing,
                extendAccessMonths: form.extendAccessMonths === ''
                    ? null
                    : Number(form.extendAccessMonths)
            };
            await adminApi().put(`/admin/schools/${editSchoolId}/platform-billing`, body);
            setMsg(t('platformBilling:superAdminSaved'));
            await loadOverview();
            await openEdit(editSchoolId);
        } catch (e) {
            setMsg(e.response?.data?.message || t('platformBilling:superAdminSaveError'));
        }
        setSaving(false);
    }

    if (role !== 'Admin') {
        return (
            <div style={{ padding: '40px', textAlign: 'center' }}>
                <p>{t('platformBilling:superAdminDenied')}</p>
                <a href="/">{t('platformBilling:superAdminHome')}</a>
            </div>
        );
    }

    const q = filter.trim().toLowerCase();
    const filtered = schools.filter((s) =>
        !q
        || (s.schoolName || '').toLowerCase().includes(q)
        || (s.ownerEmail || '').toLowerCase().includes(q)
        || (s.planDisplayName || '').toLowerCase().includes(q)
    );

    const selectedPlan = (detail?.catalogPlans || plans).find((p) => p.id === Number(form?.planId));

    return (
        <OwnerPageLayout
            title={t('platformBilling:superAdminTitle')}
            subtitle={t('platformBilling:superAdminSubtitle')}
            maxWidth={1200}
        >
                {msg && (
                    <p style={{
                        padding: '10px 14px', background: '#eef2ff', borderRadius: '8px',
                        color: '#4338ca', marginBottom: '16px'
                    }}>
                        {msg}
                    </p>
                )}

                <div style={{ ...card, marginBottom: '20px' }}>
                    <h3 style={{ margin: '0 0 12px' }}>{t('platformBilling:superAdminCatalog')}</h3>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                        {plans.map((p) => (
                            <div key={p.id} style={{
                                padding: '10px 14px', background: '#f8fafc', borderRadius: '10px',
                                border: '1px solid #e2e8f0', fontSize: '13px'
                            }}>
                                <strong>{p.displayName}</strong>
                                <div>
                                    {t('platformBilling:superAdminPerMonth', {
                                        price: fmt(p.monthlyPrice),
                                        extra: fmt(p.extraStudentPricePerMonth)
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <input
                    type="search"
                    placeholder={t('platformBilling:superAdminSearch')}
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    style={{
                        width: '100%', maxWidth: '400px', padding: '10px 14px',
                        borderRadius: '10px', border: '1px solid #ddd', marginBottom: '16px'
                    }}
                />

                {loading && <p>{t('common:loading')}</p>}

                {!loading && (
                    <div style={{ overflowX: 'auto', ...card, padding: 0 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                            <thead>
                                <tr style={{ background: '#f1f5f9', textAlign: 'left' }}>
                                    <th style={th}>{t('platformBilling:superAdminColSchool')}</th>
                                    <th style={th}>{t('platformBilling:superAdminColOwner')}</th>
                                    <th style={th}>{t('platformBilling:superAdminColPlan')}</th>
                                    <th style={th}>{t('platformBilling:superAdminColPrice')}</th>
                                    <th style={th}>{t('platformBilling:superAdminColStudents')}</th>
                                    <th style={th}>{t('platformBilling:superAdminColStatus')}</th>
                                    <th style={th} />
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((s) => (
                                    <tr key={s.schoolId} style={{ borderTop: '1px solid var(--sp-gray-100)' }}>
                                        <td style={td}>{s.schoolName}</td>
                                        <td style={td}>
                                            <div>{s.ownerEmail || '—'}</div>
                                            {s.ownerName && (
                                                <div style={{ fontSize: '12px', color: '#888' }}>{s.ownerName}</div>
                                            )}
                                        </td>
                                        <td style={td}>
                                            {s.planDisplayName || '—'}
                                            {s.hasCustomPricing && (
                                                <span style={{
                                                    marginLeft: '6px', fontSize: '11px',
                                                    background: '#fef3c7', color: '#b45309',
                                                    padding: '2px 6px', borderRadius: '6px'
                                                }}>
                                                    {t('platformBilling:superAdminCustomBadge')}
                                                </span>
                                            )}
                                        </td>
                                        <td style={td}>{fmt(s.effectiveMonthlyPrice)} ₽</td>
                                        <td style={td}>
                                            {s.currentActiveStudents}/{s.baseStudentLimit ?? '—'}
                                        </td>
                                        <td style={td}>{s.platformStatus}</td>
                                        <td style={td}>
                                            <button type="button" onClick={() => openEdit(s.schoolId)}
                                                style={btnPrimary}>
                                                {t('platformBilling:superAdminConfigure')}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {editSchoolId && form && detail && (
                    <div style={{
                        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        zIndex: 2000, padding: '20px'
                    }}>
                        <div style={{
                            ...card, maxWidth: '520px', width: '100%',
                            maxHeight: '90vh', overflowY: 'auto'
                        }}>
                            <h2 style={{ margin: '0 0 4px' }}>{detail.school.name}</h2>
                            <p style={{ margin: '0 0 16px', color: '#666', fontSize: '13px' }}>
                                {detail.school.ownerEmail}
                            </p>

                            <label style={lbl}>{t('platformBilling:superAdminPlanLabel')}</label>
                            <select style={inp} value={form.planId}
                                onChange={(e) => onPlanChange(e.target.value)}>
                                {(detail.catalogPlans || plans).map((p) => (
                                    <option key={p.id} value={p.id}>{p.displayName}</option>
                                ))}
                            </select>
                            {selectedPlan && (
                                <p style={{ fontSize: '12px', color: '#888', margin: '4px 0 12px' }}>
                                    {t('platformBilling:superAdminCatalogHint', {
                                        price: fmt(selectedPlan.monthlyPrice),
                                        limit: selectedPlan.baseStudentLimit
                                    })}
                                </p>
                            )}

                            <label style={lbl}>{t('platformBilling:superAdminBillingPeriod')}</label>
                            <select style={inp} value={form.billingPeriod}
                                onChange={(e) => setForm({ ...form, billingPeriod: e.target.value })}>
                                <option value="monthly">{t('platformBilling:superAdminMonthly')}</option>
                                <option value="annual">{t('platformBilling:superAdminAnnual')}</option>
                            </select>

                            <label style={lbl}>{t('platformBilling:superAdminBaseLimit')}</label>
                            <input style={inp} type="number" value={form.baseStudentLimit}
                                onChange={(e) => setForm({ ...form, baseStudentLimit: e.target.value })} />

                            <label style={lbl}>{t('platformBilling:superAdminExtraPrice')}</label>
                            <input style={inp} type="number" value={form.extraStudentPricePerMonth}
                                onChange={(e) => setForm({ ...form, extraStudentPricePerMonth: e.target.value })} />

                            <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <input type="checkbox" checked={form.hasCustomPricing}
                                    onChange={(e) => setForm({ ...form, hasCustomPricing: e.target.checked })} />
                                {t('platformBilling:superAdminCustomPricing')}
                            </label>

                            {form.hasCustomPricing && (
                                <>
                                    <label style={lbl}>{t('platformBilling:superAdminCustomMonthly')}</label>
                                    <input style={inp} type="number" value={form.customMonthlyPrice}
                                        onChange={(e) => setForm({ ...form, customMonthlyPrice: e.target.value })} />
                                    <label style={lbl}>{t('platformBilling:superAdminCustomAnnual')}</label>
                                    <input style={inp} type="number" value={form.customAnnualPrice}
                                        onChange={(e) => setForm({ ...form, customAnnualPrice: e.target.value })} />
                                </>
                            )}

                            <label style={lbl}>{t('platformBilling:superAdminNote')}</label>
                            <textarea style={{ ...inp, minHeight: '60px' }} value={form.adminPricingNote}
                                onChange={(e) => setForm({ ...form, adminPricingNote: e.target.value })} />

                            <label style={lbl}>{t('platformBilling:superAdminExtend')}</label>
                            <input style={inp} type="number" min="0" placeholder={t('platformBilling:superAdminExtendPlaceholder')}
                                value={form.extendAccessMonths}
                                onChange={(e) => setForm({ ...form, extendAccessMonths: e.target.value })} />

                            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                                <button type="button" onClick={save} disabled={saving} style={btnPrimary}>
                                    {saving ? t('platformBilling:superAdminSaving') : t('common:save')}
                                </button>
                                <button type="button" onClick={closeEdit} style={btnSecondary}>{t('common:close')}</button>
                            </div>
                        </div>
                    </div>
                )}
        </OwnerPageLayout>
    );
}

const th = { padding: '12px 14px', fontWeight: 600 };
const td = { padding: '12px 14px' };
const lbl = { display: 'block', fontSize: '13px', fontWeight: 600, margin: '12px 0 4px', color: '#334155' };
const inp = {
    width: '100%', padding: '10px 12px', borderRadius: '8px',
    border: '1px solid #cbd5e1', boxSizing: 'border-box'
};
const btnPrimary = {
    padding: '8px 14px', background: '#4f46e5', color: '#fff',
    border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600
};
const btnSecondary = {
    padding: '8px 14px', background: '#f1f5f9', color: '#334155',
    border: '1px solid #cbd5e1', borderRadius: '8px', cursor: 'pointer'
};
